// ###### IMPORTS
import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { createAssetLocation, updateAssetLocation } from '../../viewServices/assets_api';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

const formatApiError = (data) => {
  if (!data) return 'Failed to save.';
  if (typeof data === 'string') return data;
  if (data.detail) {
    const d = data.detail;
    return Array.isArray(d) ? d.join(' ') : String(d);
  }
  const parts = [];
  Object.keys(data).forEach((k) => {
    const v = data[k];
    if (Array.isArray(v)) parts.push(...v.map((x) => String(x)));
    else if (v != null) parts.push(String(v));
  });
  return parts.length ? parts.join(' ') : 'Failed to save.';
};

const emptyForm = () => ({
  location_name: '',
  type: 'other',
  is_active: true,
});

// ###### COMPONENT
/** `location` null/undefined = create mode; object with id = edit mode. */
const ModalEditAssetLocation = ({ isOpen, location, onClose, onSaved }) => {
  const isCreate = !location?.id;
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const applyLocation = useCallback(() => {
    if (!location?.id) {
      setForm(emptyForm());
      return;
    }
    setForm({
      location_name: location.location_name ?? '',
      type: location.type != null ? String(location.type) : 'other',
      is_active: !!location.is_active,
    });
  }, [location]);

  useEffect(() => {
    if (!isOpen) return;
    applyLocation();
  }, [isOpen, location, applyLocation]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.location_name?.trim();
    if (!name) {
      toast.error('Location name is required.');
      return;
    }
    const typeVal = (form.type ?? '').trim().slice(0, 20) || 'other';
    const payload = {
      location_name: name,
      type: typeVal,
      is_active: !!form.is_active,
    };
    try {
      setSubmitting(true);
      if (isCreate) {
        await createAssetLocation(payload);
        toast.success('Location added.');
      } else {
        await updateAssetLocation(location.id, payload);
        toast.success('Location updated.');
      }
      await Promise.resolve(onSaved?.());
      onClose?.();
    } catch (err) {
      const fallback = isCreate ? 'Failed to add location.' : 'Failed to update location.';
      toast.error(formatApiError(err.response?.data) || err.message || fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex justify-center items-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md relative max-h-[90vh] flex flex-col">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 pr-2">
            {isCreate ? 'Add location' : 'Edit location'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-gray-500 hover:text-gray-700 shrink-0 p-1 rounded-lg hover:bg-gray-100"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-4 py-3 space-y-4 text-sm">
            <p className="text-xs text-gray-500">
              This Location Catalog only contains suggestions for pickers. When using, it copies the value
              as string into new records - there is no link to historical records.
              {!isCreate && (
                <>
                  {' '}
                  Editing or removing this entry does not change existing assets.
                </>
              )}
            </p>

            <div>
              <label htmlFor="loc-form-name" className="block text-xs font-medium text-gray-600 mb-1">
                Location name
              </label>
              <input
                id="loc-form-name"
                name="location_name"
                value={form.location_name}
                onChange={handleChange}
                maxLength={100}
                disabled={submitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-600 focus:border-green-600"
                required
              />
            </div>

            <div>
              <label htmlFor="loc-form-type" className="block text-xs font-medium text-gray-600 mb-1">
                Type
              </label>
              <input
                id="loc-form-type"
                name="type"
                value={form.type}
                onChange={handleChange}
                maxLength={20}
                disabled={submitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-600 focus:border-green-600"
                placeholder="e.g. cash, brokerage"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                disabled={submitting}
                className="rounded border-gray-300 text-green-700 focus:ring-green-600"
              />
              <span className="text-sm text-gray-800">Active (shown in pickers)</span>
            </label>
          </div>

          <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0 bg-gray-50 rounded-b-2xl">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-xl bg-green-700 hover:bg-green-800 text-white text-sm font-medium disabled:opacity-50"
            >
              {isCreate ? 'Add' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEditAssetLocation;
